import styled from "@emotion/styled";
import { PageLayout } from "../../shared/layouts/PageLayout";
import { useNavigate } from "react-router";
import { adminDashboardBreadcrumbs } from "../../features/AdminDashboard/admin-dashboard-breadcrumbs";
import { useGetUsers } from "../../queries/users/useGetUsers";
import { useUsersStore } from "../../store/users.store";
import { UsersList } from "@features/Users/UsersList";
import { FiltersNavbar } from "@features/Users/FiltersNavbar";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useCallback } from "react";
import { useAdminResetPasswordAction } from "@features/Users/useAdminResetPasswordAction";
import { useAdminImpersonateAction } from "@features/Users/useAdminImpersonateAction";

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.5rem;
  width: 100%;
  position: relative;

  ${(p) => p.theme.media.mobileDown} {
    grid-template-columns: 1fr;
  }

  &[aria-disabled="true"] {
    pointer-events: none;
    opacity: 0.5;
    * {
      pointer-events: none;
    }
  }
`;

const breadcrumbs = {
  current: adminDashboardBreadcrumbs.users,
  previous: [adminDashboardBreadcrumbs.dashboard],
};

export function Users() {
  const usersQueryParams = useUsersStore((state) => state.usersQueryParams);
  const updateUsersQueryParams = useUsersStore(
    (state) => state.updateUsersQueryParams
  );
  const navigate = useNavigate();

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useGetUsers({
      ...usersQueryParams,
    });

  const pageData =
    data && data.pages.length > 0 ? data.pages[usersQueryParams.page] : null;
  const users = pageData ? pageData.users : [];
  const isLastPage = pageData ? !pageData.nextToken : true;

  const { resetPassword, isPending: isResettingPassword } =
    useAdminResetPasswordAction();
  const { impersonate, isPending: isImpersonating } =
    useAdminImpersonateAction();

  const handleResetPasswordClick = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;
    resetPassword({ email: user.email });
  };

  const handleImpersonateClick = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;
    impersonate({ email: user.email, name: user.name });
  };

  const handleViewProfileClick = (userId: string) => {
    navigate(`/admin/users/${userId}`);
  };

  const handleEditUserClick = (userId: string) => {
    navigate(`/admin/users/${userId}/edit`);
  };

  const handleUserLogsClick = (userId: string) => {
    navigate(`/admin/users/${userId}/thread-logs`);
  };

  const handleUserSubscriptionClick = (userId: string) => {
    navigate(`/admin/users/${userId}/subscription`);
  };

  const handleCreateUserClick = () => {
    navigate(`/admin/users/create`);
  };

  const handleNextPage = useCallback(async () => {
    if (!hasNextPage) return;

    if ((data?.pages?.length || 0) <= usersQueryParams.page + 1) {
      await fetchNextPage();
    }

    updateUsersQueryParams({ page: usersQueryParams.page + 1 });
  }, [
    hasNextPage,
    data,
    usersQueryParams.page,
    updateUsersQueryParams,
    fetchNextPage,
  ]);

  const handlePreviousPage = useCallback(() => {
    if (usersQueryParams.page === 0) return;
    updateUsersQueryParams({ page: usersQueryParams.page - 1 });
  }, [usersQueryParams.page, updateUsersQueryParams]);

  return (
    <PageLayout
      breadcrumbs={breadcrumbs}
      topActions={
        <Button
          onClick={handleCreateUserClick}
          variant="outlined"
          size="small"
          startIcon={<AddIcon fontSize="small" />}
        >
          Add new user
        </Button>
      }
    >
      <FiltersNavbar
        usersQueryParams={usersQueryParams}
        updateUsersQueryParams={updateUsersQueryParams}
      />
      <Container aria-disabled={isFetchingNextPage}>
        <UsersList
          users={users}
          isLoadingUsers={isLoading}
          usersQueryParams={usersQueryParams}
          isFetchingNextPage={isFetchingNextPage}
          isLastPage={isLastPage}
          handleNextPage={handleNextPage}
          handlePreviousPage={handlePreviousPage}
          handleViewProfileClick={handleViewProfileClick}
          handleEditUserClick={handleEditUserClick}
          handleUserLogsClick={handleUserLogsClick}
          handleUserSubscriptionClick={handleUserSubscriptionClick}
          handleResetPasswordClick={handleResetPasswordClick}
          handleImpersonateClick={handleImpersonateClick}
          isResettingPassword={isResettingPassword}
          isImpersonating={isImpersonating}
        />
      </Container>
    </PageLayout>
  );
}
